import { VIDEO_LIMITS } from './schema';
import { addLayer, backgroundIndex, coversPage } from './operations';

// A page's timeline (contract 2.13.0). Every video layer is a clip: `trim` is the part of
// its file it plays (seconds in the file) and `at` when it starts on the page. A clip shows
// while it plays, both ends included, so a clip starting where another ends covers it from
// that frame on. The page lasts until its last visible clip ends (a still page lasts 0); the
// export holds the last frame up to Instagram's 3 s minimum. Every time here is in seconds.
const EPSILON = 1e-3;
const round3 = value => Math.round(value * 1000) / 1000;

export const isVideo = layer => layer?.type === 'video';
export const clipLength = layer => layer.trim.end - layer.trim.start;
export const clipEnd = layer => layer.at + clipLength(layer);
export const pageVideos = page => (page?.layers || []).filter(isVideo);
export const hasVideo = page => pageVideos(page).some(layer => layer.visible);
export const documentHasVideo = doc => !!doc?.pages.some(hasVideo);
export const pageDuration = page => round3(Math.max(0, ...pageVideos(page).filter(layer => layer.visible).map(clipEnd)));
export const exportDuration = page => Math.max(VIDEO_LIMITS.minPageSeconds, pageDuration(page));

// Whether a layer shows at `time`: every layer that is not a video always does.
export const isActive = (layer, time = 0) => !isVideo(layer) || (time >= layer.at - EPSILON && time <= clipEnd(layer) + EPSILON);
// The second of its file a clip shows at page `time` (its first or last frame outside it).
export const sourceTime = (layer, time = 0) => Math.min(layer.trim.end, Math.max(layer.trim.start, layer.trim.start + (time - layer.at)));
// The page as it shows at `time`: the clips not playing then are left out (the same object
// when there is nothing to leave out).
export function pageAt(page, time = 0) {
  if (!page || !page.layers.some(layer => !isActive(layer, time))) return page;
  return { ...page, layers: page.layers.filter(layer => isActive(layer, time)) };
}

// The page's background clips: videos covering the page, which play one after the other.
export const backgroundClips = (page, doc) => pageVideos(page).filter(layer => coversPage(layer, doc));
// Where the next background clip starts: where the background's last one ends.
export const sequenceEnd = (page, doc) => round3(Math.max(0, ...backgroundClips(page, doc).map(clipEnd)));
// The seconds a new clip can last: a background clip goes after the background's last one,
// any other starts at 0.
export const roomFor = (page, doc, { background = false } = {}) => round3(Math.max(0, VIDEO_LIMITS.pageSeconds - (background ? sequenceEnd(page, doc) : 0)));

// A new video in a page: as its background, full-bleed, joined after the background's last
// clip and laid above it; otherwise over the page from its start.
export function insertVideo(doc, pageId, layer, { background = false } = {}) {
  const page = doc.pages.find(item => item.id === pageId) || doc.pages[0];
  if (!background) return addLayer(doc, page.id, { ...layer, at: 0 });
  const at = sequenceEnd(page, doc);
  const room = VIDEO_LIMITS.pageSeconds - at;
  if (room < Math.min(VIDEO_LIMITS.minClipSeconds, layer.source.duration)) return doc;
  const trim = { start: layer.trim.start, end: round3(Math.min(layer.trim.end, layer.trim.start + room)) };
  const placed = { ...layer, x: 0, y: 0, width: doc.width, height: doc.height, rotation: 0, trim, at };
  return addLayer(doc, page.id, placed, backgroundIndex(page, doc));
}

// A clip cut again (`trim`, and `at` when its start edge moved). A background clip keeps its
// sequence joined: the background clips that started where it ended move with its new end.
export function retrimClip(doc, layerId, { trim, at }) {
  const page = doc.pages.find(item => item.layers.some(layer => layer.id === layerId));
  const layer = page?.layers.find(item => item.id === layerId);
  if (!layer) return doc;
  const next = { ...layer, trim, ...(at === undefined ? {} : { at }) };
  const delta = clipEnd(next) - clipEnd(layer);
  const background = backgroundClips(page, doc);
  const followers = background.includes(layer) && Math.abs(delta) > EPSILON
    ? new Set(background.filter(other => other.id !== layer.id && other.at >= clipEnd(layer) - 0.01).map(other => other.id)) : new Set();
  const layers = page.layers.map(item => {
    if (item.id === layerId) return next;
    return followers.has(item.id) ? { ...item, ...moveClip(item, item.at + delta) } : item;
  });
  return { ...doc, pages: doc.pages.map(item => (item === page ? { ...page, layers } : item)) };
}

// The clip moved on the timeline: it starts at `at`, kept within the page's 60 s.
export const moveClip = (layer, at) => ({ at: round3(Math.min(Math.max(0, at), Math.max(0, VIDEO_LIMITS.pageSeconds - clipLength(layer)))) });

// One edge of the clip dragged by `delta` seconds on the timeline. The start edge moves the
// clip's first frame and where it starts together (the rest stays where it was); the end edge
// moves its last frame. A clip keeps at least half a second, inside its file and the page.
export function trimEdge(layer, edge, delta) {
  const { start, end } = layer.trim;
  const shortest = Math.min(VIDEO_LIMITS.minClipSeconds, layer.source.duration);
  if (edge === 'start') {
    const next = Math.min(end - shortest, Math.max(0, start + delta, start - layer.at, end - VIDEO_LIMITS.pageSeconds));
    return { trim: { start: round3(next), end }, at: round3(layer.at + (next - start)) };
  }
  const next = Math.max(start + shortest, Math.min(layer.source.duration, end + delta, start + VIDEO_LIMITS.pageSeconds - layer.at));
  return { trim: { start, end: round3(next) } };
}

// The trim dialog's selection of a file kept valid: inside the file, at least half a second
// (or the whole file when shorter) and at most `max` seconds. `moved` is what the creator
// moved: an edge (`start`, `end`) holds the other one, the window (`both`) keeps its length.
export function fitSelection({ start, end }, { duration, max = VIDEO_LIMITS.pageSeconds, moved = 'end' }) {
  const shortest = Math.min(VIDEO_LIMITS.minClipSeconds, duration);
  const longest = Math.max(shortest, Math.min(max, duration));
  if (moved === 'both') {
    const length = Math.min(Math.max(end - start, shortest), longest);
    const from = Math.min(Math.max(0, start), duration - length);
    return { start: round3(from), end: round3(from + length) };
  }
  if (moved === 'start') {
    const from = Math.min(Math.max(0, start, end - longest), end - shortest);
    return { start: round3(Math.max(0, from)), end: round3(end) };
  }
  const to = Math.max(Math.min(duration, end, start + longest), start + shortest);
  return { start: round3(start), end: round3(Math.min(duration, to)) };
}

// The times a dragged clip edge snaps to: the page's start, the playhead and the other clips'
// edges.
export const snapTimes = (page, exceptId, playhead = null) => [0, ...(playhead === null ? [] : [playhead]),
  ...pageVideos(page).filter(layer => layer.id !== exceptId && layer.visible).flatMap(layer => [layer.at, clipEnd(layer)])];
export function snapTime(value, times, tolerance) {
  let best = null;
  times.forEach(time => { if (Math.abs(time - value) <= tolerance && (best === null || Math.abs(time - value) < Math.abs(best - value))) best = time; });
  return best;
}

// The timeline's rows: each clip in the first row where it overlaps no other, bottom layer
// first, so a background's joined clips share one row and what plays over them gets its own.
export function clipLanes(page) {
  const lanes = [];
  pageVideos(page).forEach(layer => {
    const free = lanes.find(lane => lane.every(other => clipEnd(other) <= layer.at + EPSILON || clipEnd(layer) <= other.at + EPSILON));
    if (free) free.push(layer); else lanes.push([layer]);
  });
  return lanes;
}
